import { api } from "~/utils/api"

import { trackEvent } from "./useTrackingService"
import { toast } from "react-toastify"
import { showErrorToast } from "~/utils/toaster"
import { DemandPlanEventType } from "~/constants/demandPlanEventType"
import { isEqual } from "lodash"
import { useMemo } from "react"

export const useCreateDemandPlan = () => {
  const utils = api.useUtils()
  return api.demandPlan.create.useMutation({
    onSuccess: () => {
      utils.demandPlan.getDemandPlans.invalidate()
      trackEvent("Demand Plan Created")
      toast.success("Demand plan created successfully")
    },
  })
}

export const useCreateDemandPlanVersion = () => {
  const utils = api.useUtils()
  return api.demandPlan.createVersion.useMutation({
    onSuccess: () => {
      utils.demandPlan.getDemandPlans.invalidate()
      trackEvent("Demand Plan Created")
      toast.success("Demand plan created successfully")
    },
  })
}

export const useUpdateDemandPlan = () => {
  const utils = api.useUtils()
  return api.demandPlan.update.useMutation({
    onSuccess: () => {
      utils.demandPlan.getDemandPlans.invalidate()
      trackEvent("Demand Plan Updated")
      toast.success("Demand plan updated successfully")
    },
  })
}

export const useEditDemandPlan = () => {
  const utils = api.useUtils()
  return api.demandPlan.editDemandPlan.useMutation({
    onSuccess: () => {
      utils.demandPlan.getDemandPlans.invalidate()
      trackEvent("Demand Plan Edited")
      toast.success("Demand plan updated successfully")
    },
  })
}

export const useDeleteDemandPlan = () => {
  const utils = api.useUtils()
  return api.demandPlan.deleteDemandPlan.useMutation({
    onSuccess: () => {
      utils.demandPlan.getDemandPlans.invalidate()
      trackEvent("Demand Plan Deleted")
      toast.success("Demand plan deleted successfully")
    },
  })
}

export const useGetDemandPlan = (demandPlanId: string, enabled: boolean = true) => {
  return api.demandPlan.getDemandPlans.useQuery(
    {},
    {
      enabled,
      select: (data) => data.find((dp) => dp.id === demandPlanId),
    },
  )
}

export const useGetDemandPlansForCollectionIds = (collectionIds: string[]) => {
  const { data: allDemandPlans, isLoading, isFetching } = api.demandPlan.getDemandPlans.useQuery({})

  const filteredDemandPlans = useMemo(() => {
    if (!allDemandPlans || collectionIds.length === 0) return []
    const sortedCollectionIds = [...collectionIds].sort()
    return allDemandPlans
      .filter((dp) => isEqual([...dp.sourceCollection].sort(), sortedCollectionIds))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [allDemandPlans, collectionIds])

  return { data: filteredDemandPlans, isLoading, isFetching }
}

export const useGetDemandPlanSalesForecast = (demandPlanId: string, enabled: boolean) => {
  return api.demandPlan.getDemandPlanSalesForecast.useQuery({ demandPlanId }, { enabled: enabled })
}

export const useGetTrimmedVariants = (demandPlanId: string) => {
  return api.demandPlan.getTrimmedVariants.useQuery({ demandPlanId })
}

export const useUpsertTrimmedVariants = () => {
  const utils = api.useUtils()
  return api.demandPlan.upsertTrimmedVariants.useMutation({
    onSuccess: () => {
      utils.demandPlan.getTrimmedVariants.invalidate()
      trackEvent("Trimmed variants successfully updated")
      toast.success("Trimmed variants successfully updated")
    },
  })
}

// NOT_FOUND means the plan was replaced or deleted elsewhere; refetching the plan list lets
// DemandPlanVersionSelector switch the session to the current plan.
const handleStaleDemandPlanError = (
  err: { message: string; data?: { code?: string } | null },
  utils: ReturnType<typeof api.useUtils>,
  fallbackMessage: string,
) => {
  if (err.data?.code === "NOT_FOUND") {
    utils.demandPlan.getDemandPlans.invalidate()
    showErrorToast(err)
    return
  }
  toast.error(fallbackMessage)
}

export const useUpsertSalesForecastChanges = () => {
  const utils = api.useUtils()
  return api.demandPlan.upsertSalesForecastChanges.useMutation({
    onSuccess: (_data, variables) => {
      trackEvent("Sales forecast changes successfully updated")
      toast.success("Sales forecast changes successfully updated")
      // Return the promise so the mutation stays pending until the refetch
      // lands — otherwise per-call onSuccess callbacks (e.g. closing the
      // adjustments drawer) fire while the grid still holds stale variants.
      return utils.demandPlan.getDemandPlanSalesForecast.invalidate({
        demandPlanId: variables.demandPlanId,
      })
    },
    onError: (err) => {
      handleStaleDemandPlanError(err, utils, "Your changes didn't get applied. Please try again.")
    },
  })
}

interface VariantForForecastUpdate {
  variantId: string
  enabledSalesMetrics: string[]
}

export const useUpdateManualFinalForecast = (demandPlanId: string | undefined) => {
  const upsertMutation = useUpsertSalesForecastChanges()
  const createEvent = useCreateDemandPlanEvent()

  return {
    isPending: upsertMutation.isPending,
    update: (
      variant: VariantForForecastUpdate,
      newValue: number | null,
      oldDisplayValue: number,
      newDisplayValue: number,
    ) => {
      if (!demandPlanId) return

      upsertMutation.mutate(
        {
          demandPlanId,
          changes: [
            {
              variantId: variant.variantId,
              enabledSalesMetrics: variant.enabledSalesMetrics,
              manualFinalForecast: newValue,
            },
          ],
        },
        {
          onSuccess: () => {
            createEvent.mutate({
              demandPlanId,
              description: DemandPlanEventType.DemandAdjusted,
              variantIds: [variant.variantId],
              oldValue: oldDisplayValue,
              newValue: newDisplayValue,
              changedFields: ["manualFinalForecast"],
            })
          },
        },
      )
    },
  }
}

export const useRevertSalesForecastForEvent = () => {
  const utils = api.useUtils()
  return api.demandPlan.revertSalesForecastForEvent.useMutation({
    onSuccess: (_data, variables) => {
      utils.demandPlan.getDemandPlanSalesForecast.invalidate({
        demandPlanId: variables.demandPlanId,
      })
      utils.demandPlan.getEvents.invalidate({ demandPlanId: variables.demandPlanId })
      trackEvent("Demand Plan Forecast Reverted")
      toast.success("Change reverted successfully")
    },
    onError: (err) => {
      handleStaleDemandPlanError(err, utils, "Failed to revert change. Please try again.")
    },
  })
}

export const useCreateDemandPlanEvent = () => {
  const utils = api.useUtils()
  return api.demandPlan.createEvent.useMutation({
    onSuccess: (data) => {
      utils.demandPlan.getEvents.invalidate({ demandPlanId: data.demandPlanId })
      trackEvent("Demand Plan Event Created")
    },
  })
}

export const useGetDemandPlanEvents = (demandPlanId: string, enabled: boolean = true) => {
  return api.demandPlan.getEvents.useQuery({ demandPlanId }, { enabled })
}

export const useGetDemandPlanEvent = (eventId: number | undefined, demandPlanId?: string) => {
  return api.demandPlan.getEvents.useQuery(
    { demandPlanId: demandPlanId! },
    {
      enabled: !!demandPlanId && eventId != null,
      select(data) {
        return data.find((event) => event.id === eventId)
      },
    },
  )
}

export const useGetDemandPlanSeasonalityProfile = (
  profileName?: string,
  enabled: boolean = true,
) => {
  return api.demandPlan.getSeasonalityProfile.useQuery({ profileName }, { enabled })
}

export const useGetDemandPlanSeasonality = (demandPlanId: string, enabled: boolean = true) => {
  return api.demandPlan.getSeasonality.useQuery({ demandPlanId }, { enabled })
}

export const useUpsertDemandPlanSeasonality = () => {
  const utils = api.useUtils()
  return api.demandPlan.upsertSeasonality.useMutation({
    onSuccess: (data) => {
      utils.demandPlan.getSeasonality.invalidate({ demandPlanId: data.demandPlanId })
      trackEvent("Demand Plan Seasonality Created or Updated")
    },
  })
}
